import type { ColumnDef } from '@tanstack/react-table';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { DataTable, SortableHeader } from '@/components/data-table';

/**
 * TablePage — Standard full-height page layout for data table views.
 *
 * Provides a consistent layout across all list pages:
 * - Full-height flex container (fills the space the app layout gives it)
 * - Page title with optional record count
 * - Optional description
 * - Optional header action slot (e.g. "Invite User" button)
 * - DataTable with search, sorting, pagination (rows 25/50/100, default 50)
 * - Pagination footer flush with the table bottom border
 * - Error state display
 *
 * For server-side pagination, pass `pagination`, `onPaginationChange`, and `onSearchChange`.
 */

interface TablePageProps<TData, TValue> {
  title: string;
  description?: string;
  columns?: ColumnDef<TData, TValue>[];
  data?: TData[];
  total?: number;
  isLoading?: boolean;
  error?: Error | null;
  searchColumn?: string;
  searchPlaceholder?: string;
  onRowClick?: (row: TData) => void;
  emptyMessage?: string;
  errorMessage?: string;
  headerAction?: React.ReactNode;
  children?: React.ReactNode;
  /** Server-side pagination state. */
  pagination?: PaginationState;
  /** Server-side pagination callback. */
  onPaginationChange?: (pagination: PaginationState) => void;
  /** Server-side search callback. */
  onSearchChange?: (value: string) => void;
  /**
   * Controlled value for the search input. Pass when search term lives
   * outside the table (e.g. URL state) so back/forward syncs the input.
   */
  searchValue?: string;
  /** Optional filter bar rendered between search input and table. */
  filterBar?: React.ReactNode;
  /**
   * Optional content rendered between the page header and the data
   * table. Use for a static panel that should always be visible
   * regardless of pagination.
   */
  aboveTable?: React.ReactNode;
  /** Server-side sorting state. */
  sorting?: SortingState;
  /** Server-side sorting callback. */
  onSortingChange?: (sorting: SortingState) => void;
  /** Extra classes on the `<table>` element (e.g. `table-fixed`). */
  tableClassName?: string;
}

export function TablePage<TData, TValue>({
  title,
  description,
  columns,
  data,
  total,
  isLoading = false,
  error,
  searchColumn,
  searchPlaceholder,
  onRowClick,
  emptyMessage,
  errorMessage = 'Failed to load data. Please try again.',
  headerAction,
  children,
  pagination,
  onPaginationChange,
  onSearchChange,
  searchValue,
  filterBar,
  aboveTable,
  sorting,
  onSortingChange,
  tableClassName,
}: TablePageProps<TData, TValue>): React.ReactElement {
  return (
    // `h-full`, not `h-screen`: the app layout already bounds this to the
    // space under the impersonation banner, and `h-screen` pushed the
    // pagination footer below the fold by the banner's height. The bottom
    // padding is lighter than the top so the footer sits near the edge.
    <div className="flex h-full min-h-0 flex-col px-6 pt-6 pb-3">
      <div className="mb-6 flex shrink-0 items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {title}
            {!isLoading && total != null && (
              <span className="ml-1 text-lg font-normal text-muted-foreground">({total})</span>
            )}
          </h1>
          {description && <p className="text-muted-foreground">{description}</p>}
        </div>
        {headerAction}
      </div>

      {aboveTable && <div className="mb-4 shrink-0">{aboveTable}</div>}

      {columns && data ? (
        error ? (
          <div className="rounded-md bg-destructive/10 p-4 text-destructive">{errorMessage}</div>
        ) : (
          <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            searchColumn={searchColumn}
            searchPlaceholder={searchPlaceholder}
            onRowClick={onRowClick}
            emptyMessage={emptyMessage}
            fillHeight
            totalRows={total}
            pagination={pagination}
            onPaginationChange={onPaginationChange}
            onSearchChange={onSearchChange}
            searchValue={searchValue}
            filterBar={filterBar}
            sorting={sorting}
            onSortingChange={onSortingChange}
            tableClassName={tableClassName}
          />
        )
      ) : null}

      {children}
    </div>
  );
}

// Re-export SortableHeader for convenience
export { SortableHeader };
